import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClipboardList, Camera, Share2, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    icon: ClipboardList,
    title: "This is your job",
    body: "A job holds your punch-list reports. Add a report and list out what needs doing — that's your checklist.",
  },
  {
    icon: Camera,
    title: "Add your punch-list photos",
    body: "Tap an item and add a photo of what needs doing — that's your reference shot so the crew knows exactly what you mean.",
  },
  {
    icon: Share2,
    title: "Send it to your crew",
    body: "Hit Share and text the link. They open each item, snap proof it's done, and send it back — no app, no login.",
  },
  {
    icon: CheckCircle2,
    title: "Verify from your phone",
    body: "Watch the progress climb to 100% as proof rolls in. You verify it's done without ever driving to the site.",
  },
];

// One-time walkthrough shown the first time a sandbox visitor opens their job.
export function SandboxOnboarding({ open, onClose }) {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const Icon = step.icon;
  const last = i === STEPS.length - 1;

  const finish = () => { setI(0); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="max-w-sm overflow-hidden p-0" data-testid="sandbox-onboarding">
        <div className="flex flex-col items-center px-6 pb-6 pt-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-500">
            <Icon className="h-8 w-8" />
          </div>
          <h3 className="mt-5 font-heading text-xl font-bold" data-testid="onboarding-title">{step.title}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{step.body}</p>

          <div className="mt-6 flex items-center justify-center gap-1.5" data-testid="onboarding-dots">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-emerald-500" : "w-1.5 bg-muted"}`}
              />
            ))}
          </div>

          <div className="mt-6 flex w-full items-center gap-2">
            {i > 0 && (
              <Button
                variant="outline"
                data-testid="onboarding-back"
                onClick={() => setI((n) => n - 1)}
                className="h-11 flex-1 gap-1"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            <Button
              data-testid="onboarding-next"
              onClick={() => (last ? finish() : setI((n) => n + 1))}
              className="h-11 flex-1 gap-1 bg-emerald-500 font-bold hover:bg-emerald-600"
            >
              {last ? "Got it — let's go" : <>Next <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </div>
          {!last && (
            <button data-testid="onboarding-skip" onClick={finish} className="mt-3 text-xs font-medium text-muted-foreground hover:text-foreground">
              Skip
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
